import { Trans, useLingui } from '@lingui/react';
import { Card } from '@startuptools/ui/Card';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { Table } from '@startuptools/ui/Table';
import { Typography } from '@startuptools/ui/Typography';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useSummaryShowSuspenseQuery } from './SummaryShow.graphql';
import { BigNumber } from 'bignumber.js';
import { compact } from 'lodash-es';

export const ShareIssueSummaryShow = ({ shareIssueId }: { shareIssueId: string }) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const {
    data: { shareIssue },
  } = useSummaryShowSuspenseQuery({ variables: { companyId, shareIssueId }, fetchPolicy: 'cache-and-network' });

  const currency = shareIssue.currency;

  return (
    <Card>
      <Table sx={{ tableLayout: 'auto', maxWidth: 700 }}>
        <tbody>
          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Price per share" />:
              </Typography>
            </td>
            <td>
              <NumberFormat
                locale={i18n.locale}
                variant="currency"
                currency={currency}
                minimumFractionDigits={2}
                maximumFractionDigits={6}
              >
                {shareIssue.pricePerShare}
              </NumberFormat>
            </td>
          </tr>
          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Quota value" />:
              </Typography>
            </td>
            <td>
              <NumberFormat
                locale={i18n.locale}
                variant="currency"
                currency={currency}
                minimumFractionDigits={0}
                maximumFractionDigits={6}
              >
                {shareIssue.quotaValue}
              </NumberFormat>
            </td>
          </tr>

          <tr>
            <th colSpan={2}>
              <Trans id="Before the share issue" />:
            </th>
          </tr>
          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Number of shares before" />:
              </Typography>
            </td>
            <td>
              <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                {shareIssue.numberOfSharesBefore}
              </NumberFormat>
            </td>
          </tr>
          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Share capital before" />:
              </Typography>
            </td>
            <td>
              {shareIssue.numberOfSharesBefore && (
                <NumberFormat
                  locale={i18n.locale}
                  variant="currency"
                  currency={currency}
                  minimumFractionDigits={0}
                  maximumFractionDigits={6}
                >
                  {new BigNumber(shareIssue.numberOfSharesBefore).times(shareIssue.quotaValue ?? 0)}
                </NumberFormat>
              )}
            </td>
          </tr>
          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Valuation before the share issue" />:
              </Typography>
            </td>
            <td>
              {shareIssue.numberOfSharesBefore && (
                <NumberFormat locale={i18n.locale} variant="currency" currency={currency}>
                  {new BigNumber(shareIssue.numberOfSharesBefore).times(shareIssue.pricePerShare ?? 0)}
                </NumberFormat>
              )}
            </td>
          </tr>

          <tr>
            <th colSpan={2}>
              <Trans id="Minimum" />:
            </th>
          </tr>
          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Minimum number of new shares" />:
              </Typography>
            </td>
            <td>
              <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                {shareIssue.minNumberOfShares}
              </NumberFormat>
            </td>
          </tr>
          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Total amount raise if minimum threshold is reached" />:
              </Typography>
            </td>
            <td>
              {shareIssue.minNumberOfShares && (
                <NumberFormat
                  locale={i18n.locale}
                  variant="currency"
                  currency={currency}
                  minimumFractionDigits={0}
                  maximumFractionDigits={6}
                >
                  {new BigNumber(shareIssue.minNumberOfShares).times(shareIssue.pricePerShare ?? 0)}
                </NumberFormat>
              )}
            </td>
          </tr>
          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Valuation at the lowest subscription" />:
              </Typography>
            </td>
            <td>
              {shareIssue.minNumberOfShares && shareIssue.numberOfSharesBefore && (
                <NumberFormat locale={i18n.locale} variant="currency" currency={currency}>
                  {new BigNumber(shareIssue.minNumberOfShares)
                    .plus(shareIssue.numberOfSharesBefore)
                    .times(shareIssue.pricePerShare ?? 0)}
                </NumberFormat>
              )}
            </td>
          </tr>

          <tr>
            <th colSpan={2}>
              <Trans id="Maximum" />:
            </th>
          </tr>
          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Maximum number of new shares" />:
              </Typography>
            </td>
            <td>
              <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                {shareIssue.maxNumberOfShares}
              </NumberFormat>
            </td>
          </tr>
          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Total amount raise if maximum threshold is reached" />:
              </Typography>
            </td>
            <td>
              {shareIssue.maxNumberOfShares && (
                <NumberFormat
                  locale={i18n.locale}
                  variant="currency"
                  currency={currency}
                  minimumFractionDigits={0}
                  maximumFractionDigits={6}
                >
                  {new BigNumber(shareIssue.maxNumberOfShares).times(shareIssue.pricePerShare ?? 0)}
                </NumberFormat>
              )}
            </td>
          </tr>
          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Valuation at the highest subscription" />:
              </Typography>
            </td>
            <td>
              {shareIssue.maxNumberOfShares && shareIssue.numberOfSharesBefore && (
                <NumberFormat locale={i18n.locale} variant="currency" currency={currency}>
                  {new BigNumber(shareIssue.maxNumberOfShares)
                    .plus(shareIssue.numberOfSharesBefore)
                    .times(shareIssue.pricePerShare ?? 0)}
                </NumberFormat>
              )}
            </td>
          </tr>
          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Share capital increase at full subscription" />:
              </Typography>
            </td>
            <td>
              {shareIssue.maxNumberOfShares && (
                <NumberFormat
                  locale={i18n.locale}
                  variant="currency"
                  currency={currency}
                  minimumFractionDigits={0}
                  maximumFractionDigits={6}
                >
                  {new BigNumber(shareIssue.maxNumberOfShares).times(shareIssue.quotaValue ?? 0)}
                </NumberFormat>
              )}
            </td>
          </tr>

          <tr>
            <th colSpan={2}>
              <Trans id="Payment" />:
            </th>
          </tr>

          <tr>
            <td>
              <Typography level="title-sm">
                <Trans id="Payment may be made in the following ways" />:
              </Typography>
            </td>
            <td>
              {compact([
                shareIssue.paymentKindCash ? i18n.t('Cash') : null,
                shareIssue.paymentKindOffset ? i18n.t('Offset') : null,
                shareIssue.paymentKindNonCash ? i18n.t('In-kind') : null,
              ]).join(', ')}
            </td>
          </tr>
        </tbody>
      </Table>
    </Card>
  );
};
